import React, { useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import "./MyData.scss";
import { Typography } from "../../../components/__common/Typography/Typography";
import { LinkButton, TTSButton, Button } from "../../../components/__common/_controls";
import { Svg, SVG_ICONS } from "../../../components/__common/Svg/Svg";
import { Summary } from "./Sections/Summary/Summary";
import { ManageApps } from "./Sections/ManageApps/ManageApps";
import { UsageGuide } from "./UsageGuide/UsageGuide";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { MyDataState } from "../../../store/reducers/mydata-reducer";
import { useEffect } from "react";
import { useMyDataHooks } from "../../../hooks/mydata-hooks";
import { RequestDetailsModal } from "./RequestDetailsModal/RequestDetailsModal";
import { ROUTES } from "../../../configs/routes";
import { GlobalSiteLinkPopup } from "../../../components/GlobalSiteLinkPopup/GlobalSiteLinkPopup";
import { Trans, useTranslation } from "react-i18next";
import { UserState } from "../../../store/reducers/user-reducer";
import {
  EVENT_CATEGORY_MY_DATA,
  EVENT_MY_DATA_ACTION_MORE_INFORMATION,
} from "../../../constants/analytics";
import Loading from "../../../assets/images/gif/sdap-loading.json";
import { HelpTipsPoupup } from "../../../components/HelpTipsPopup/HelpTipsPopup";
import { Container } from "../../../components/Grid/Grid";
import { URLS } from "../../../constants/urls";
import { Cookie } from "../../../services/storage";
import { COOKIE_GST_ATKN } from "../../../constants/storage";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import { useHub } from "../../../hooks/hub-hooks";
import classNames from "classnames";
import { ServiceRequestToastError } from "./ServiceRequestToastError/ServiceRequestToastError";
import { useUserHooks } from "../../../hooks/user-hooks";
import { UtilsUser } from "../../../utils/UtilsUser";
import GoogleAnalytics from "../../../utils/GoogleAnalytics";
import {
  TURKEY_LOCALSITE_LINK,
  TURKEY_SA_COUNTRY_CODE,
} from "../../../constants/countryDefaults";
import FocusTrapping from "../../../components/__hoc/FocusTrapping";
import Lottie from "lottie-react";

export const MyData = () => {
  // third party hooks
  const { t } = useTranslation();
  const history = useHistory();
  const params: any = useParams();

  // custom hooks
  const {
    loadSummary,
    closeGlobalSiteLinkPopup,
    resetServiceRequestErrorToast,
    validateDeepLinkRequestNumber,
  } = useMyDataHooks();
  const { hasLocalSite } = useHub();
  const { pathname } = useLocation();
  const { isDigitalLegacy } = useUserHooks();
  const dispatch = useDispatch();
  // local states
  const [isGuideOpen, setGuideOpen] = useState<boolean>(false);
  const [displayToast, setDisplayToast] = useState<boolean>(false);
  // global states
  const {
    isSummaryLoading,
    summary,
    isSummaryLoaded,
    isGlobalSiteLinkOpen,
    serviceRequestToastError,
  } = useSelector<ApplicationState, MyDataState>((state) => state.myData);
  const { userCountryCode, userData, userRegion } = useSelector<
    ApplicationState,
    UserState
  >((state) => state.user);
  const { languageTrans, selectedCountry, isDarkMode, isLanguageReady } =
    useSelector<ApplicationState, LayoutState>((state) => state.layout);

  // variables
  const isEmailLogin = Cookie.get(COOKIE_GST_ATKN);

  const saCountry = userData.countryCode;

  useEffect(() => {
    validateDeepLinkRequestNumber(params.requestNumber);
  }, []);
  // Will handle deeplink scenarios for TR SA countrycode
  useEffect(() => {
    if (saCountry && saCountry === TURKEY_SA_COUNTRY_CODE) {
      window.location.replace(TURKEY_LOCALSITE_LINK);
    }
  }, [saCountry]);

  useEffect(
    () => {
      const currentURL = window.location.pathname;
      if (currentURL.includes(URLS.OLD_MYDATA)) {
        history.replace(URLS.MYDATA);
      }
      if (
        !isSummaryLoaded &&
        userData.countryCode !== "" &&
        languageTrans !== "" &&
        isLanguageReady
      ) {
        loadSummary();
      }
    },

    // eslint-disable-next-line
    [languageTrans, userData.countryCode, languageTrans, isLanguageReady]
  );

  useEffect(() => {
    if (hasLocalSite && !isSummaryLoading && !isDigitalLegacy) {
      history.replace(
        `${summary.count.total_count > 0 ? ROUTES.MYDATA_HISTORY : ROUTES.ROOT}`
      );
    }
  }, [summary, selectedCountry, userCountryCode]);

  useEffect(
    () => {
      const currentURL = window.location.pathname;
      const reqNum = currentURL.split("/").pop();
      const ticketNumberRegex = /SP|SPDL-(DEV|STG|PRE|[0-9A-Z]*)/g;
      // reqnum checker
      if (reqNum && reqNum.match(ticketNumberRegex)) {
        if (hasLocalSite && !isDigitalLegacy) {
          history.push(`${ROUTES.MYDATA_HISTORY}/${reqNum}`);
        } else {
          history.push(`${ROUTES.MYDATA}/${reqNum}`);
        }
      }
    },
    // eslint-disable-next-line
    [userCountryCode, selectedCountry, pathname]
  );

  useEffect(() => {
    if (isGuideOpen) {
      document.getElementById("usage-guide-modal-tts")?.focus();
    }
  }, [isGuideOpen]);
  useEffect(() => {
    if (serviceRequestToastError.hasRendered) {
      setDisplayToast(true);
    }

    return () => {
      setDisplayToast(false);
      handleErrorToastClose();
    };
  }, [serviceRequestToastError.hasRendered]);

  const handleErrorToastClose = () => {
    if (
      serviceRequestToastError.isError ||
      serviceRequestToastError.hasRendered
    ) {
      resetServiceRequestErrorToast();
      setDisplayToast(false);
    }
  };

  const handleMENALocalSiteRedirect = () => {
    const localSiteLink = UtilsUser.getMENALocalSiteLink(saCountry);
    if (localSiteLink) {
      window.open(localSiteLink, "_self");
    }
  };

  const getCountrySpecificComponent = (
    country: string,
    isUserRestricted = false
  ) => {
    //  CHINA - CHILD RESTRICTED
    if (country === "CHN" && !isDigitalLegacy) {
      return (
        <Container className="mydata__china">
          <Svg
            icon={SVG_ICONS.MYDATA_CHINA}
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <Typography variant="title3" className="mydata__china__title">
            {t("PRIVACY_MYDATA_MSG_CHINA")}
          </Typography>
          <Typography variant="body2" className="mydata__china__text">
            <Trans
              i18nKey="PRIVACY_MYDATA_MSG_CHINA_CNPP_LINK"
              components={{
                a: (
                  // eslint-disable-next-line
                  <a target="_blank" href="https://privacy.samsung.com.cn/" />
                ),
              }}
            />
          </Typography>
        </Container>
      );
       //  KOREA - CHILD RESTRICTED
    } else if (country === "KOR" && isUserRestricted) {
      return (
        <Container className="mydata__china">
          <Svg
            icon={
              !isDarkMode
                ? SVG_ICONS.MYDATA_KOREA_CHILD
                : SVG_ICONS.MYDATA_KOREA_CHILD_DARK
            }
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <Typography variant="title3" className="mydata__china__title">
            {t("PRIVACY_MYDATA_MSG_CHILDREN_KO")}
          </Typography>
          <Typography variant="body2" className="mydata__china__text">
            {t("PRIVACY_MYDATA_MSG_CHILDREN_KO2")}
          </Typography>
          <Typography variant="body2" className="mydata__china__text__bulleted">
            <Trans
              i18nKey="PRIVACY_MYDATA_MSG_CHILDREN_KO3"
              components={{
                a: (
                  // eslint-disable-next-line
                  <a
                    target="_blank"
                    href="https://www.samsung.com/sec/support"
                  />
                ),
              }}
            />
          </Typography>
        </Container>
      );
       //  USA - CHILD RESTRICTED
    } else if (country === "USA" && isUserRestricted) {
      return (
        <Container className="mydata__china">
          <Svg
            icon={
              !isDarkMode
                ? SVG_ICONS.MYDATA_KOREA_CHILD
                : SVG_ICONS.MYDATA_KOREA_CHILD_DARK
            }
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <Typography variant="body2" className="mydata__china__altText">
            <Trans
              i18nKey="PRIVACY_MYDATA_MSG_CHILDREN_USA"
              components={{
                a: (
                  // eslint-disable-next-line
                  <a
                    target="_blank"
                    href="https://www.samsung.com/us/privacy"
                  />
                ),
              }}
            />
          </Typography>
        </Container>
      );
       //  EU - CHILD RESTRICTED
    } else if (userRegion.isSAFromEurope && isUserRestricted) {
      return (
        <Container className="mydata__china">
          <Svg
            icon={
              !isDarkMode
                ? SVG_ICONS.MYDATA_KOREA_CHILD
                : SVG_ICONS.MYDATA_KOREA_CHILD_DARK
            }
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <Typography variant="body2" className="mydata__china__altText">
            <Trans
              i18nKey="PRIVACY_MYDATA_MSG_CHILDREN_EU"
              components={{
                a: (
                  // eslint-disable-next-line
                  <a
                    target="_blank"
                    href="https://www.samsung.com/request-desk"
                  />
                ),
              }}
            />
          </Typography>
        </Container>
      );
       //  MENA - CHILD RESTRICTED
    } else if (userRegion.isSAFromMENA && isUserRestricted) { 
      return (
        <Container className="mydata__china">
          <Svg
            icon={
              !isDarkMode
                ? SVG_ICONS.MYDATA_KOREA_CHILD
                : SVG_ICONS.MYDATA_KOREA_CHILD_DARK
            }
            width={320}
            height={320}
            className="mydata__china__icon"
          />
          <div className="mydata_mena_container">
          <Typography variant="body2" className="mydata__china__altText mydata_mena_text">
          <Trans i18nKey="PRIVACY_MYDATA_MSG_CHILDREN_MENA"/>
          </Typography>
            <Button
              variant="solid"
              className="button"
              tooltip={t("PRIVACY_HUB_COUNTRY_BUTTON")}
              testId="mena-local-site-btn"
              title={t("PRIVACY_HUB_COUNTRY_BUTTON")}
              onClick={handleMENALocalSiteRedirect}
            />

          </div>
         
        </Container>
      );
    } else {
      return <></>;
    }
  };

  const renderSummarySection = () => {
    return ((userData.countryCode === "KOR" ||
      userData.countryCode === "USA" ||
      userRegion.isSAFromEurope ||
      userRegion.isSAFromMENA) &&
      userRegion.isRestricted) ||
      (userData.countryCode === "CHN" && !isDigitalLegacy) ? (
      getCountrySpecificComponent(userData.countryCode, userRegion.isRestricted)
    ) : (
      <Summary
        summary={summary}
        isFromChina={userData.countryCode === "CHN" && !isDigitalLegacy}
        isRestricted={userRegion.isRestricted}
        saCountry={userData.countryCode}
      />
    );
  };

  const renderSummaryIsLoading = () => {
    return (
      <div className="summary-loading-container">
        <Lottie
          className="summary-loading-container__loading"
          animationData={Loading}
          loop={true}
          style={{ width: 60, height: 60 }}
        />
      </div>
    );
  };

  return (
    <>
      {!!params.requestNumber && (
        <RequestDetailsModal
          headerTestid="requestDetail-onhide-button"
          show={!!params.requestNumber}
          requestNumber={params.requestNumber}
          onHide={() => history.push(ROUTES.MYDATA)}
        />
      )}
      {displayToast && (
        <ServiceRequestToastError
          isSAUnverified={serviceRequestToastError.isSAUnverifiedError}
          show={displayToast}
          onClose={handleErrorToastClose}
        />
      )}
      <div
        className={classNames("mydata", {
          "child-account": userRegion.isRestricted,
        })}
      >
        <HelpTipsPoupup />
        {isGlobalSiteLinkOpen && !isEmailLogin && (
          <GlobalSiteLinkPopup onHide={closeGlobalSiteLinkPopup} />
        )}

        {isGuideOpen && (
          <FocusTrapping
            isActive={isGuideOpen}
            onHandleClose={() => {
              setGuideOpen(false);
              document.getElementById("mydata-usage-guide-button")?.focus();
            }}
            modalId="usage-guide-container"
          >
            <UsageGuide
              headertestid="guide-close-button"
              open={isGuideOpen}
              onClickClose={() => {
                setGuideOpen(false);
                document.getElementById("mydata-usage-guide-button")?.focus();
              }}
            />
          </FocusTrapping>
        )}
        <div className="mydata__title-section">
          <Typography variant="h2" ss className="mydata__title-section__title">
            {t("PRIVACY_MYDATA_PAGE_TITLE")}
            <TTSButton
              value={
                t("PRIVACY_MYDATA_PAGE_TITLE") +
                ". " +
                t("PRIVACY_MYDATA_PAGE_DESC")
              }
            />
          </Typography>
          <Typography
            variant="body1"
            weight="w400"
            className="mydata__title-section__sub"
          >
            {t("PRIVACY_MYDATA_PAGE_DESC")}
          </Typography>
          {userData.countryCode !== "CHN" && (
            <LinkButton
              id="mydata-usage-guide-button"
              to="#"
              title={t("PRIVACY_MYDATA_PAGE_BTN_GUIDE")}
              iconPosition="left"
              icon={SVG_ICONS.GUIDE}
              underlined={false}
              iconSize={24}
              size="lg"
              svgIconToolTip={t("PRIVACY_MYDATA_PAGE_BTN_GUIDE")}
              textClassName={"mydata__title-section__more"}
              onClick={() => {
                GoogleAnalytics.triggerEvent(
                  EVENT_CATEGORY_MY_DATA,
                  EVENT_MY_DATA_ACTION_MORE_INFORMATION
                );
                setGuideOpen(true);
              }}
            />
          )}
        </div>
        {/* SUMMARY SECTION */}
        {isSummaryLoading ? renderSummaryIsLoading() : renderSummarySection()}
        {/* MANAGE APP SECTION */}
        {userData.countryCode !== "CHN" && !userRegion.isRestricted && (
          <ManageApps />
        )}
      </div>
    </>
  );
};
